'use client';

import { useEffect, useState } from 'react';
import type { DisplayAvailability } from '@/lib/availability-types';

export interface AvailabilityRequestItem {
  sku: string;
  /** Defaults to 1. Pass the cart-line quantity so over-available lines are flagged. */
  quantity?: number;
}

/**
 * Live backorder/availability lookup for the storefront (PDP, cart).
 *
 * POSTs the given items (sku + quantity) to the public availability endpoint and
 * returns a Map keyed by SKU. Pass the real cart-line quantity so a line that
 * requests more than is available shows the same verdict checkout enforces.
 * Fail-open: on any error the map stays empty and no warning is shown, so a ShipEdge
 * blip never paints a scary state.
 */
export function useAvailability(
  items: AvailabilityRequestItem[]
): Map<string, DisplayAvailability> {
  const [availability, setAvailability] = useState<Map<string, DisplayAvailability>>(new Map());

  // Stabilise the effect dependency: re-fetch when the SKU set OR any quantity
  // changes. (SKUs are assumed free of ',' and ':' — same assumption as the cart.)
  const key = items.map((item) => `${item.sku}:${item.quantity ?? 1}`).join(',');

  useEffect(() => {
    const list = key
      ? key.split(',').map((pair) => {
          const sep = pair.lastIndexOf(':');
          return { sku: pair.slice(0, sep), quantity: Number(pair.slice(sep + 1)) };
        })
      : [];
    if (list.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/products/availability', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ items: list }),
        });
        if (!res.ok) return;
        const json = (await res.json()) as { data?: { results?: DisplayAvailability[] } };
        const results = json?.data?.results ?? [];
        if (!cancelled) {
          setAvailability(new Map(results.map((r) => [r.sku, r])));
        }
      } catch {
        // Fail-open: leave the map empty, render no warning.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [key]);

  return availability;
}
